import _debounce from "lodash/debounce";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Talk from "talkjs";
import { getCommunityChatConversations } from "../../../utils/chat-util";
import { getTalkJsLocale } from "../../../utils/talkjs-locale";
import { CommunityChatMounted } from "./components/main/CommunityChatMounted";
import { CommunityChatUnmounted } from "./components/main/CommunityChatUnmounted";

const TALK_JS_APP_ID = "tMXnzHXl";

const MSBCommunityChat = ({ api, locals }) => {
  const { activeUser, activeCommunityChatClientsList, query = {} } = locals;
  const { initId } = query;

  const { _id: userId, name: name, webAppTheme = "dark" } = activeUser;
  const { i18n } = useTranslation();

  const chatboxEl = useRef();
  const session = useRef(null);
  const chatbox = useRef(null);
  const debouncedHandleNewMessage = useRef(null); //to handle the debounce function in case of receive a new message
  const [conversations, setConversations] = useState({
    readChannels: [],
    readDirects: [],
    unreadChats: [],
    lastConversationId: null,
  });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [talkLoaded, markTalkLoaded] = useState(false);
  const [mobileChatOpened, setMobileChatOpened] = useState(false);
  const [isLoading, setIsloading] = useState(true);
  const [shouldMount, setShouldMount] = useState(false);

  /**
   * Function to get all the push conversations
   */
  const getConversations = async (selectFirstConversation, checkMount) => {
    const data = await getCommunityChatConversations(api, userId);
    setConversations(data);
    setIsloading(false);
    if (checkMount) {
      //we are checking if there is at least 1 conversation to mount the chat
      const { readChannels = [], readDirects = [], unreadChats = [] } = data;
      const allChatsEmpty = [readChannels, readDirects, unreadChats].every(
        (chatArray) => chatArray.length === 0
      );
      // Signal that we should mount once the DOM is ready. A deep link still
      // needs the chatbox mounted when the conversation list comes back empty.
      if (!allChatsEmpty || initId) {
        setShouldMount(true);
      }
    }
    if (selectFirstConversation) {
      // A deep link (team Go to Chat, or the check-conversation redirect)
      // must win over the coach's last opened conversation. This runs after
      // the chatbox exists; the initId effect below can fire earlier, before
      // TalkJS has created it.
      if (initId) {
        selectConversation(initId, setSelectedConversation, chatbox);
        if (window.innerWidth < 640) {
          setMobileChatOpened(true);
        }
      } else if (data?.lastConversationId?.length > 0) {
        selectConversation(
          data.lastConversationId,
          setSelectedConversation,
          chatbox
        );
      }
    }
  };

  /**
   * function to init the talkjs environment, and add the message listener
   */
  const loadChat = async () => {
    let actualSession = null;
    if (!session.current) {
      const currentUser = await new Talk.User({
        id: userId,
        name,
        role: webAppTheme === "dark" ? "msbdarktheme" : "msbwhitetheme",
        locale: getTalkJsLocale(i18n.language),
      });

      actualSession = await new Talk.Session({
        appId: TALK_JS_APP_ID,
        me: currentUser,
      });

      session.current = actualSession;
      chatbox.current = await actualSession.createChatbox(
        {
          messageField: { spellcheck: true },
        },
        {
          selected: null,
        },
        { showLocationButton: false }
      );

      //get the conversations for the first time
      getConversations(true, true);

      //Get the conversations if the user receives a new message and is not from the opened chat
      const handleNewMessage = (messageProp) => {
        const newConversationId = messageProp?.conversation?.id;
        const openedChat = chatbox?.current.currentConversation?.id;
        const isByMe = messageProp?.isByMe;

        if (newConversationId !== openedChat && !isByMe) {
          getConversations(false, false);
        }
      };
      // Here we are executing the code 4 secs after a new message comes, if the user receives another message in those 5 seconds, the time is going to be reseted
      // To start counting againg, this is to avoid so many calls if the user receives a lot of messages
      debouncedHandleNewMessage.current = _debounce(handleNewMessage, 4000);
      actualSession?.onMessage((messageProp) => {
        debouncedHandleNewMessage.current(messageProp);
      });
    }
  };

  /**
   * UseEffect to call the TalkJS functions if it's already mounted and ready to use
   */
  useEffect(() => {
    if (!talkLoaded) return;

    loadChat();
  }, [talkLoaded]);

  /**
   * UseEffect to mount the chatbox when DOM is ready and we have conversations
   */
  useEffect(() => {
    if (shouldMount) {
      chatbox.current.mount(chatboxEl.current);
      setShouldMount(false);
    }
  }, [shouldMount]);

  /**
   * UseEffect to handle the initialization and finalization of the TalkJS mount
   */
  useEffect(() => {
    if (typeof window !== "undefined") {
      initializeTalkJs(talkLoaded, markTalkLoaded);
      initializeExternalStyling();
      return () => {
        session.current?.destroy();
        session.current = null;
        chatbox.current = null;
        if (debouncedHandleNewMessage.current) {
          debouncedHandleNewMessage.current.cancel();
        }
        finalizeExternalStyling();
      };
    }
  }, []);

  /**
   * UseEffect to handle the styles for mobile screens
   */
  useEffect(() => {
    const pageWrapper = document.getElementById("page-wrapper");

    if (mobileChatOpened) {
      addMobileClasses(pageWrapper);
    } else {
      removeMobileClasses(pageWrapper);
    }
  }, [mobileChatOpened]);

  /**
   * Select a deep-linked conversation once TalkJS is ready. initId alone is
   * not enough: on a full page load this effect runs before the chatbox
   * exists, and getConversations would otherwise open lastConversationId.
   */
  useEffect(() => {
    if (!initId || !talkLoaded || !chatbox.current) return;

    setSelectedConversation(initId);
    chatbox.current.select(initId);
    if (window.innerWidth < 640) {
      setMobileChatOpened(true);
    }
  }, [initId, talkLoaded]);

  useEffect(() => {
    if (session.current) {
      session.current.currentUser.set({ locale: getTalkJsLocale(i18n.language) });
    }
  }, [i18n.language]);

  return (
    <main
      className={
        mobileChatOpened
          ? "Main Community-Chat main-mobile-chat-opened"
          : "Main Community-Chat main-mobile-chat-closed"
      }
    >
      {talkLoaded && activeCommunityChatClientsList && !isLoading ? (
        <CommunityChatMounted
          locals={locals}
          api={api}
          conversations={conversations}
          setConversations={setConversations}
          selectedConversation={selectedConversation}
          setSelectedConversation={setSelectedConversation}
          chatbox={chatbox}
          chatboxEl={chatboxEl}
          mobileChatOpened={mobileChatOpened}
          setMobileChatOpened={setMobileChatOpened}
        />
      ) : (
        <CommunityChatUnmounted locals={locals} webAppTheme={webAppTheme} />
      )}
    </main>
  );
};

MSBCommunityChat.propTypes = {
  api: PropTypes.any,
  locals: PropTypes.object,
};

export default MSBCommunityChat;

/**
 * Function to remove Intercom Chat and apply the styling for the header
 */
const initializeExternalStyling = () => {
  // Hide the Intercom Chat since it overlaps the chat buttons
  window.Intercom && window.Intercom("update", { hide_default_launcher: true });
  window.Intercom && window.Intercom("hide");

  // hide element from dom and adding border in the header
  const arrow = document.querySelector("#profile-dropdown picture");
  const profileBox = document.querySelector("#profile-dropdown summary");
  const options = document.querySelector("#profile-dropdown > div");
  const header = document.querySelector('header[role="main"]');
  header.style.borderBottom = "1px solid var(--white-20)";
  arrow.style.display = "none";
  profileBox.style.cursor = "initial";
  options.style.visibility = "hidden";
};

/**
 * Function to show Intercom Chat again and remove the gived styling for the header
 */
const finalizeExternalStyling = () => {
  // Show again the Intercom Chat
  window.Intercom &&
    window.Intercom("update", { hide_default_launcher: false });
  // hide element from dom, remove the border in the footer and remove the mobile classes
  const arrow = document.querySelector("#profile-dropdown picture");
  const options = document.querySelector("#profile-dropdown > div");
  const profileBox = document.querySelector("#profile-dropdown summary");
  const pageWrapper = document.getElementById("page-wrapper");
  const header = document.querySelector('header[role="main"]');

  removeMobileClasses(pageWrapper);
  arrow.style.display = "block";
  options.style.visibility = "unset";
  profileBox.style.cursor = "pointer";
  header.style.borderBottom = "";
};

/**
 * Function to know when talkJs is ready to be used
 */
const initializeTalkJs = (talkLoaded, markTalkLoaded) => {
  if (!talkLoaded) {
    Talk.ready.then(() => {
      markTalkLoaded(true);
    });
  }
};

/**
 * Function to add the classes for mobile screens
 */
const addMobileClasses = (pageWrapper) => {
  pageWrapper.classList.add("page-wrapper-mobile-chat-opened");
};

/**
 * Function to remove the classes for mobile screens
 */
const removeMobileClasses = (pageWrapper) => {
  pageWrapper.classList.remove("page-wrapper-mobile-chat-opened");
};

/**
 * Function to open the chatbox with the last conversation
 * @param {*} lastConversationId
 * @param {*} setSelectedConversation
 * @param {*} chatbox
 */
const selectConversation = (
  lastConversationId,
  setSelectedConversation,
  chatbox
) => {
  setSelectedConversation(lastConversationId);
  chatbox.current.select(lastConversationId);
};
